import { useState } from "react";
import { Pulse, CheckCircle, WarningOctagon, X } from "@phosphor-icons/react";
import { useSystemHealth } from "../../context/SystemHealthContext";

/* Deep health pill — reads from SystemHealthContext (single source of truth)
   so the same data also gates the Generate buttons in StepByStepComposer. */
export default function SystemHealthPill() {
  const { overall, checks, loading, refresh } = useSystemHealth();
  const [open, setOpen] = useState(false);

  const data = { overall, checks, checked_at: null };
  const badge = overall === "ok"
    ? { label: "System OK", color: "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]", Icon: CheckCircle }
    : overall === "degraded"
      ? { label: "Env drift", color: "bg-amber-500/15 text-amber-500", Icon: WarningOctagon }
      : { label: "Checking…", color: "bg-muted/30 text-muted-foreground", Icon: Pulse };
  const Icon = badge.Icon;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border hairline text-[11px] ${badge.color}`}
        title="Click for a deep-health breakdown — ffmpeg, Mongo, GridFS and universal-key status."
        data-testid="system-health-pill"
      >
        <Icon size={12} weight="duotone" />
        <span className="font-medium">{badge.label}</span>
      </button>
      {open && (
        <HealthModal data={data} loading={loading} refresh={refresh}
          onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function HealthModal({ data, loading, refresh, onClose }) {
  const checks = data?.checks || {};
  const rows = [
    { id: "ffmpeg",        label: "ffmpeg",            critical: true },
    { id: "mongo",         label: "MongoDB",            critical: true },
    { id: "gridfs",        label: "GridFS (social bucket)", critical: true },
    { id: "emergent_key",  label: "Emergent LLM key",   critical: true },
    { id: "elevenlabs_key", label: "ElevenLabs key",    critical: false },
    { id: "heygen_key",    label: "HeyGen key",         critical: false },
  ];
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-2xl w-full p-5 space-y-4 max-h-[88vh] overflow-y-auto"
        data-testid="system-health-modal">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="eyebrow">Help · System health</div>
            <h2 className="font-serif text-2xl mt-1">Environment self-check</h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-xl">
              Verifies that every dependency the Storyboard pipeline needs is reachable
              <i> before</i> you spend credits. Generate buttons auto-disable when any
              critical check fails.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost px-2 py-1.5 text-xs"
            aria-label="Close" data-testid="system-health-close">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="space-y-1.5" data-testid="system-health-rows">
          {rows.map((r) => {
            const c = checks[r.id] || {};
            const s = c.status || "unknown";
            const good = s === "ok" || s === "present";
            return (
              <div key={r.id} className="card-flat p-3 flex items-start gap-3"
                data-testid={`health-row-${r.id}`}>
                <span className={`mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full ${good ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : (r.critical ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground")}`}>
                  {good ? <CheckCircle size={12} weight="bold" /> : <WarningOctagon size={12} weight="bold" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{r.label}</span>
                    <span className={`text-[10px] uppercase tracking-wider ${good ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}>
                      {s}
                    </span>
                    {!r.critical && <span className="text-[10px] text-muted-foreground italic">optional</span>}
                  </div>
                  {c.version && <div className="text-[10px] text-muted-foreground font-mono mt-1 truncate">{c.version}</div>}
                  {c.masked && <div className="text-[10px] text-muted-foreground font-mono mt-1">key: {c.masked}</div>}
                  {c.error && <div className="text-[10px] text-destructive mt-1 break-words">{c.error}</div>}
                  {c.fix && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      <b>Fix:</b> <span className="font-mono">{c.fix}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-1 border-t hairline">
          <div className="text-[10px] text-muted-foreground">
            {data?.checked_at ? `Last check · ${new Date(data.checked_at).toLocaleTimeString()}` : "Click Re-run to refresh."}
          </div>
          <button onClick={refresh} disabled={loading}
            className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-50"
            data-testid="system-health-refresh">
            {loading ? "Checking…" : "Re-run check"}
          </button>
        </div>
      </div>
    </div>
  );
}
